import React, { useState, useEffect } from "react";
import {
  format,
  addDays,
  differenceInDays,
  isValid,
  parseISO,
  startOfDay,
} from "date-fns";
import { Calendar as CalendarIcon, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { useFollowup } from "@/hooks/followup/useFollowup";
import { useDebounce } from "@/hooks/use-debounce";
import { useAutoSaveInit } from "@/hooks/useAutoSaveInit";

interface FollowUpCardProps {
  appointmentId: string;
  isCompleted?: boolean;
  parentType?: "prescription" | "meal_plan";
}

export const FollowUpCard = ({
  appointmentId,
  isCompleted = false,
  parentType = "prescription",
}: FollowUpCardProps) => {
  const {
    data: followup,
    create,
    update,
    isCreating,
    isUpdating,
  } = useFollowup(appointmentId, parentType);
  const { isInitialized, markInitialized } = useAutoSaveInit([
    appointmentId,
    parentType,
  ]);

  const [date, setDate] = useState<Date | undefined>();
  const [days, setDays] = useState<string>("");
  const [instructions, setInstructions] = useState<string>("");
  const [mode, setMode] = useState<"date" | "days">("date");

  useEffect(() => {
    if (followup && !isInitialized.current) {
      if (followup.date) {
        const parsedDate = parseISO(followup.date);
        if (isValid(parsedDate)) {
          setDate(parsedDate);
          const diff = differenceInDays(parsedDate, startOfDay(new Date()));
          setDays(diff >= 0 ? diff.toString() : "");
        }
      }
      setInstructions(followup.instructions || "");
      markInitialized();
    }
  }, [followup, isInitialized, markInitialized]);

  const debouncedDate = useDebounce(date, 1000);
  const debouncedInstructions = useDebounce(instructions, 1000);

  const handleDateSelect = (newDate: Date | undefined) => {
    setDate(newDate);
    if (newDate) {
      const diff = differenceInDays(newDate, startOfDay(new Date()));
      setDays(diff >= 0 ? diff.toString() : "");
    } else {
      setDays("");
    }
  };

  const handleClear = () => {
    setDate(undefined);
    setDays("");
  };

  const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setDays(val);
    const numDays = parseInt(val, 10);
    if (!isNaN(numDays) && numDays >= 0) {
      const newDate = addDays(startOfDay(new Date()), numDays);
      setDate(newDate);
    }
  };

  useEffect(() => {
    if (!debouncedDate) return;
    if (followup && !isInitialized.current) return;

    const formattedDate = format(debouncedDate, "yyyy-MM-dd");

    if (
      followup &&
      followup.date === formattedDate &&
      followup.instructions === debouncedInstructions
    ) {
      return;
    }

    const payload = {
      date: formattedDate,
      instructions: debouncedInstructions,
    };

    if (followup) {
      update({ id: followup.id, data: payload });
    } else {
      create({ parent_type: parentType, ...payload });
    }
  }, [debouncedDate, debouncedInstructions, followup, create, update]);

  const isSaving = isCreating || isUpdating;

  return (
    <Widget>
      <WidgetHeader
        title="Follow-up"
        hasInfo
        extras={
          isSaving ? (
            <Loader2 className="size-3 animate-spin text-muted-foreground" />
          ) : undefined
        }
      />
      <WidgetContent className="pt-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          <div className="space-y-1.5 lg:col-span-4">
            <div className="flex items-center justify-between gap-2">
              <label className="z-label text-muted-foreground">
                {mode === "date" ? "Date" : "Days from now"}
              </label>
              <div className="inline-flex items-center rounded-full bg-muted p-0.5">
                {(["date", "days"] as const).map((opt) => {
                  const active = mode === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      tabIndex={-1}
                      onClick={() => setMode(opt)}
                      disabled={isCompleted || active}
                      className={cn(
                        "z-hint rounded-full px-2 py-0.5 transition-colors disabled:opacity-100 capitalize",
                        active
                          ? "bg-card text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
            {mode === "date" ? (
              <div className="flex items-center gap-1.5">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "flex-1 justify-start text-left font-normal",
                        !date && "text-muted-foreground"
                      )}
                      disabled={isCompleted}
                    >
                      <CalendarIcon className="mr-2 size-4" />
                      {date ? format(date, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      initialFocus
                      disabled={(d) => d < new Date()}
                    />
                  </PopoverContent>
                </Popover>
                {date && !isCompleted && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={handleClear}
                    type="button"
                  >
                    <X className="size-4" />
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  min="0"
                  value={days}
                  onChange={handleDaysChange}
                  placeholder="e.g. 7"
                  disabled={isCompleted}
                  className="flex-1"
                />
                {days && !isCompleted && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={handleClear}
                    type="button"
                  >
                    <X className="size-4" />
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="space-y-1.5 lg:col-span-8">
            <label className="z-label text-muted-foreground">
              Instructions
            </label>
            <Input
              type="text"
              placeholder="e.g. with lab-reports & checkup"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              disabled={isCompleted}
              maxLength={45}
            />
            <p
              className={cn(
                "z-hint text-right",
                45 - instructions.length <= 10
                  ? "text-destructive"
                  : "text-muted-foreground"
              )}
            >
              {45 - instructions.length} left
            </p>
          </div>
        </div>
      </WidgetContent>
    </Widget>
  );
};
